import { UPLOAD_BATCH_SIZE } from './folderImageUploadRunner'

const DB_NAME = 'PhotoKingFolderUploads'
const DB_VERSION = 1
const STORE = 'jobs'

export type BatchStatus =
  | 'pending'
  | 'compressed'
  | 'uploading'
  | 'done'
  | 'failed'

export interface PersistedFileBlob {
  name: string
  type: string
  blob: Blob
}

export interface PersistedBatch {
  batchIndex: number
  status: BatchStatus
  files: PersistedFileBlob[]
  /** Kept after upload so done batches still know how many images were sent. */
  fileCount: number
}

export interface PersistedUploadJobMeta {
  jobId: string
  folderId: number
  projectId: number
  folderName: string
  compressionPercentage: number
  totalFiles: number
  uploadedFiles: number
  batchCount: number
  createdAt: number
  updatedAt: number
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE)
        }
      }
    })
  }
  return dbPromise
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

async function idbPut(key: string, value: unknown): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const req = tx.objectStore(STORE).put(value, key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const req = tx.objectStore(STORE).delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function idbGetAllKeys(): Promise<IDBValidKey[]> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).getAllKeys()
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

const metaKey = (jobId: string) => `meta:${jobId}`
const batchKey = (jobId: string, index: number) => `batch:${jobId}:${index}`

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

export function filesToPersisted(files: File[]): PersistedFileBlob[] {
  return files.map((file) => ({
    name: file.name,
    type: file.type || 'image/jpeg',
    blob: file,
  }))
}

export function persistedToFiles(records: PersistedFileBlob[]): File[] {
  return records.map(
    (r) => new File([r.blob], r.name, { type: r.type || 'image/jpeg' }),
  )
}

/** Remove stale in-progress jobs for the same folder before starting a new upload. */
export async function clearPendingJobsForFolder(
  folderId: number,
): Promise<void> {
  const jobs = await listIncompleteJobs()
  await Promise.all(
    jobs
      .filter((j) => j.folderId === folderId)
      .map((j) => deleteUploadJob(j.jobId)),
  )
}

export async function createUploadJob(params: {
  folderId: number
  projectId: number
  folderName: string
  compressionPercentage: number
  files: File[]
}): Promise<string> {
  await clearPendingJobsForFolder(params.folderId)

  const jobId = `upload-${params.folderId}-${Date.now()}`
  const fileBatches = chunkArray(params.files, UPLOAD_BATCH_SIZE)
  const now = Date.now()

  const meta: PersistedUploadJobMeta = {
    jobId,
    folderId: params.folderId,
    projectId: params.projectId,
    folderName: params.folderName,
    compressionPercentage: params.compressionPercentage,
    totalFiles: params.files.length,
    uploadedFiles: 0,
    batchCount: fileBatches.length,
    createdAt: now,
    updatedAt: now,
  }

  await idbPut(metaKey(jobId), meta)

  await Promise.all(
    fileBatches.map((files, i) => {
      const batch: PersistedBatch = {
        batchIndex: i,
        status: 'pending',
        files: filesToPersisted(files),
        fileCount: files.length,
      }
      return idbPut(batchKey(jobId, i), batch)
    }),
  )

  return jobId
}

export async function getJobMeta(
  jobId: string,
): Promise<PersistedUploadJobMeta | undefined> {
  return idbGet<PersistedUploadJobMeta>(metaKey(jobId))
}

export async function getBatch(
  jobId: string,
  batchIndex: number,
): Promise<PersistedBatch | undefined> {
  return idbGet<PersistedBatch>(batchKey(jobId, batchIndex))
}

export async function saveBatch(
  jobId: string,
  batch: PersistedBatch,
): Promise<void> {
  await idbPut(batchKey(jobId, batch.batchIndex), batch)
}

export async function updateJobProgress(
  jobId: string,
  uploadedFiles: number,
): Promise<void> {
  const meta = await getJobMeta(jobId)
  if (!meta) return
  meta.uploadedFiles = uploadedFiles
  meta.updatedAt = Date.now()
  await idbPut(metaKey(jobId), meta)
}

/** Count images in batches already marked done (source of truth after refresh). */
export async function countUploadedFilesFromBatches(
  jobId: string,
  batchCount: number,
): Promise<number> {
  let count = 0
  for (let i = 0; i < batchCount; i++) {
    const batch = await getBatch(jobId, i)
    if (batch?.status === 'done') {
      count += batch.fileCount
    }
  }
  return count
}

export async function markBatchUploaded(
  jobId: string,
  batchIndex: number,
  fileCount: number,
): Promise<number> {
  const meta = await getJobMeta(jobId)
  if (!meta) return 0

  const existing = await getBatch(jobId, batchIndex)
  if (existing?.status === 'done') {
    return meta.uploadedFiles
  }

  const uploadedFiles = meta.uploadedFiles + fileCount
  await updateJobProgress(jobId, uploadedFiles)

  const doneBatch: PersistedBatch = {
    batchIndex,
    status: 'done',
    files: [],
    fileCount,
  }
  await saveBatch(jobId, doneBatch)
  return uploadedFiles
}

export async function markBatchUploading(
  jobId: string,
  batch: PersistedBatch,
): Promise<void> {
  await saveBatch(jobId, {
    ...batch,
    status: 'uploading',
  })
}

export async function deleteUploadJob(jobId: string): Promise<void> {
  const meta = await getJobMeta(jobId)
  if (!meta) {
    await idbDelete(metaKey(jobId))
    return
  }
  for (let i = 0; i < meta.batchCount; i++) {
    await idbDelete(batchKey(jobId, i))
  }
  await idbDelete(metaKey(jobId))
}

export async function listIncompleteJobs(): Promise<PersistedUploadJobMeta[]> {
  const keys = await idbGetAllKeys()
  const jobs: PersistedUploadJobMeta[] = []
  for (const key of keys) {
    if (typeof key !== 'string' || !key.startsWith('meta:')) continue
    const meta = await idbGet<PersistedUploadJobMeta>(key)
    if (meta && meta.uploadedFiles < meta.totalFiles) {
      // Check if there are any batches left that have NOT permanently failed
      let hasRetryableBatches = false
      for (let i = 0; i < meta.batchCount; i++) {
        const batch = await getBatch(meta.jobId, i)
        if (batch && batch.status !== 'done' && batch.status !== 'failed') {
          hasRetryableBatches = true
          break
        }
      }
      if (hasRetryableBatches) {
        jobs.push(meta)
      } else {
        // If all remaining batches were rejected/failed, clean up the job so it never loops on refresh
        await deleteUploadJob(meta.jobId)
      }
    }
  }
  jobs.sort((a, b) => a.createdAt - b.createdAt)
  return jobs
}
